/**
 * The financial-account subtypes Mantle currently supports across the product —
 * the one source of truth shared by the consumer web display and the backend
 * assistant agent surface.
 *
 * Named by enum **key** (e.g. `CHECKING`), not by stored value, because the
 * layers hold different string *values* for the same subtype: the GraphQL
 * surface exposes the enum key names over the wire (type-graphql's
 * `registerEnumType(FinancialAccountSubtype)` — so web's codegen'd enum has
 * `Checking = 'CHECKING'`), while the DB stores a lowercase variant
 * (`CHECKING = 'checking'`). Each consumer resolves these keys through its own
 * `FinancialAccountSubtype` enum.
 *
 * To support a new subtype (e.g. money-market) under an already-supported
 * primary type, add its key here. The backend widens off the list alone; the
 * web also needs the subtype mapped to an account section
 * (`packages/web/src/utils/account/accountSections.ts`).
 */

/** Depository subtypes Mantle displays/answers on (checking + savings). */
export const DEPOSITORY_SUBTYPE_KEYS = ['CHECKING', 'SAVINGS'] as const;

/** Credit subtypes Mantle displays/answers on (credit card only). */
export const CREDIT_CARD_SUBTYPE_KEYS = ['CREDIT_CARD'] as const;

/**
 * Investment subtypes Mantle displays/answers on once the household's
 * investment accounts are enabled; the web files all of them under one
 * Investments section. Plaid has no rollover subtype — a rollover arrives as
 * `IRA`.
 */
export const INVESTMENT_SUBTYPE_KEYS = [
  'BROKERAGE',
  'IRA',
  'ROTH',
  'SEP_IRA',
  'SIMPLE_IRA',
  'PLAN_401K',
  'PLAN_401A',
  'PLAN_403B',
  'PLAN_457B',
  'ROTH_401K',
  'PENSION',
  'HSA',
  'PLAN_529',
  'CRYPTO_EXCHANGE',
  'TRUST',
  'UGMA',
  'UTMA',
] as const;
